/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 * created by cdmiller
 */

import math from 'mathjs';
import nerdamer from 'nerdamer/all';

export const wrapDynFormula = function(Dyn) {

    class Nodes {
        constructor( attributeName ) {
            // MathJS nodes from math.parse() then node.traverse()
            this.constants = []; // node.type ConstantNode
            this.operators = [];
            this.symbols = [];
            this.functions = [];
            this.aggregates = [];
            this.other = []; // collect note.type names in case we need them in the future

            this.clearDependencies();
            this.selfSymbol = attributeName||""; // symbol for attribute that uses this formula, if any
        }
        findNodes( parseNode ) {
            parseNode.traverse( ( node /*, path, parent*/ ) => {
                switch (node.type) {
                    case 'OperatorNode':
                        this.operators.push( node.op );
                        break;    // operators like "+"
                    case 'ConstantNode':
                        this.constants.push( node.value );
                        break;    // numeric constants like "1.2"
                    case 'SymbolNode':
                        this.symbols.push( node.name );
                        break;   // possible attribute values like "age"
                    case 'FunctionNode':
                        this.functions.push( node.name );
                        break;   // MathJS function names like "pickRandom"
                    default:
                        this.other.push( node.type );
                }
            } );
        }
        clearDependencies() {
            // clear derived lists, created by findDependencies()
            this.attributeSymbols = [];
            this.constantSymbols = [];
            this.plotSymbols = [];
            this.unknownSymbols = [];
            this.upstreamSymbols = []; // updated by findReverseDependencies()
        }
    }

    /**
     * Aggregate functions are handled by replacing a formula like 'mean( age )' with a constant like _agg_mean_age,
     * And supplying a value for that function in the scope of a formula evaluation, along with case attribute values.
     * This is the helper function to define which are the aggregate functions, and how they are calculated.
     */
    class Aggregate {
        constructor( formulaString, attributeName, functionName, calcFunction ) {
            this.formulaString = formulaString; // e.g. 'mean( age )' (as it appears in formula string)
            this.attributeName = attributeName; // e.g. 'age'
            this.functionName = functionName; // e.g. 'mean'
            this.func = calcFunction; // e.g. reference to mean(numericArray) function
            this.variableName = "_"+functionName+"_"+attributeName; // e.g. '_mean_age'
            Dyn.assert( typeof calcFunction === 'function' );
        }
        calculate( numericValues ) {
            let result = (numericValues.length >= 1) ?
                    Formula.fixPrecision( this.func( numericValues ), Dyn.dataUtil.kMissingValue) :
                    Dyn.dataUtil.kMissingValue;
            return result;
        }
        static replaceAggregatesInString( formulaString )
        {
            let aggregates = [],
                newString = formulaString.replace( new RegExp( Aggregate.getPattern(), 'g' ), replacer );
            //console.log( "replaceAggregatesInString", (newString!==formulaString)?"new string: "+newString:"unchanged: "+formulaString );
            if( aggregates.length ) {
                return { aggregates:aggregates, formulaString:newString };
            } else {
                return null;
            }

            function replacer( match, p1FunctionName, p2AttributeName, offset, string ) {
                //console.log("replaceAggregatesInString found", match, p1FunctionName, p2AttributeName, offset, "in", string );
                let agg = new Aggregate( match, p2AttributeName, p1FunctionName, Aggregate.functions[p1FunctionName] );
                aggregates.push( agg ); // save for use by calcAggregates()
                return agg.variableName; // replace match with variable name, for use by calcAggregates()
            }
        }
        static pattern = ""; // find "..mean( attribute ), etc.. in strings; build once and store here
        static getPattern() {
            if( ! Aggregate.findString ) {
                // build Regular Expression pattern to find" aggregate-function ( attribute ) " pairs,
                //      with whitespace before and after every parenthesis (to match what MathJS normal functions allow).
                //      with [a-zA-Z0-9_] as allowable function and attribute characters (as returned by _attributeIDtoSymbol())
                // Example: '...mean( age_attr )' matches
                //          '...count (height_attr)' matches
                //          ' log( age_attr )' does not match ('log' is not an aggregate function).
                let names = Object.keys( Aggregate.functions ).join( '|' );
                Aggregate.pattern = '(' + names + ')\\s*\\(\\s*(\\w+)\\s*\\)';
            }
            return Aggregate.pattern;
        }
        static functions = {
            // define the aggregate function names and formulas, for use in derived attribute formulas
            // all take an array of finite numeric values, 1+ length, with non-numeric/missing values removed
            // warning: changing function names will break previously saved formulas.
            mean:   ( a ) => { return Dyn.mathUtil.meanOfNumericArray( a ) },
            median: ( a ) => { return Dyn.mathUtil.medianOfNumericArray( a ) },
            iqr:    ( a ) => { return Dyn.mathUtil.quartilesOfNumericArray( a ).IQR },
            stdDev:    ( a ) => { return Dyn.mathUtil.stDevOfNumericArray( a ) },
            meanAvDev:    ( a ) => { return Dyn.mathUtil.meanAvDevOfNumericArray( a ) },
            variance: ( a ) => { return Dyn.mathUtil.varianceOfNumericArray( a ) },
            sum:    ( a ) => { return Dyn.mathUtil.sumOfNumericArray( a ) },
            min:    ( a ) => { return a.reduce( (g,h) => Math.min(g,h), a[0] ); },
            max:    ( a ) => { return a.reduce( (g,h) => Math.max(g,h), a[0] ); },
            count:  ( a ) => { return a.length },
        }
    }

    class Formula {

        /**
         * Constructor for a formula used to create derived attributes or to plot a function curve
         * @param formulaString
         * @param formulaSyntax -- {"LaTeX" || "MathJS" || null } -- defaults to MathJS, or converted using latexFormulaToMathJS()
         * @param constantScope -- optional numeric or string constant values to add to scope, e.g. { a:1, name:"jane" }
         * @param formulaID -- optional formula ID
         */
        constructor( formulaString, formulaSyntax, constantScope, formulaID ) {
            this.formulaString = formulaString || ""; // string value of formula
            this.compiledFormula = null; // MathJS compiled formula
            this.derivativeFormula = null; // MathJS-derived slopeOfFx formula string
            this.derivativeCompiled = null; // MathJS-derived slopeOfFx compiled formula
            this.nodes = null; // objects in parsed formula
            this.error = {};   // last error string, of various types
            this.constantScope = {};   // map of constant name:value pairs where name is a string, value is a finite number.

            if( formulaID ) {
                this.id = "" + formulaID; // optional formula ID, force to string
            }

            if( formulaSyntax === "LaTeX" ) {
                this.formulaString = this.latexFormulaToMathJS( this.formulaString );
                if( Formula.useNerdamerOnlyWherePossible || Formula.compareNerdamerToMathJS ) {
                    this.nerdamerString = this.latexFormulaToNerdamer( formulaString );
                }
            } else {
                Dyn.assert( (formulaSyntax === "MathJS") || !formulaSyntax, "Formula syntax must be 'LaTeX' or 'MathJS'" );
            }
            if( constantScope ) {
                this.constantScope = constantScope; // see also setConstant()
            }

            this.extend_MathJS_Library();
        }

        // static const
        static useNerdamerOnlyWherePossible = true;  // turn on for efficiency, turn off to revert to MathJS evaluation
        static compareNerdamerToMathJS = false;      // turn on for debugging the difference between MathJS and Nerdamer
        static dontLogDivideByZeroError = true;      // turn off to see divide by zero errors

        // from: http://mathjs.org/docs/reference/constants.html
        //      Note: this list determines which MathJS constants can be used in Tuva attribute formulas
        //      we exclude less useful formulas like "null" so we don't have to explain them to users;
        //      for now only numeric constants seem useful.  --CDM 2017-05-16
        get _mathJS_Constants()  { return [ 'e', 'E', 'i', 'pi', 'PI', 'LN2', 'LN10', 'LOG2E', 'LOG10E', 'NaN', 'phi', 'tau' ] }

    /** our custom modifications to MathJS will go here */
    extend_MathJS_Library() {
        if( ! math.hasTuvaExtentions ) {
            // add constants and functions as described in: http://mathjs.org/docs/core/extension.html
            math.import({
                hasTuvaExtentions: true,
                //myvalue: 42,

                length: function( stringOrArray ) {
                    let result = "not text";
                    switch( typeof stringOrArray ) {
                    case "string":
                        result = stringOrArray.length;
                        break;
                    case "object":
                        if( Array.isArray( stringOrArray ) ) {
                            result = stringOrArray.length;
                        }
                        break;
                    }
                    return result;
                }
            });
        }

    }

    isEmptyFormula() {
        return this.formulaString.length === 0;
    }

    getLastErrorString( errorFilterString ) {
        var error = this.error,
            str;

        switch( errorFilterString ) {
        case "parseErrorsOnly":
            str = error.parse || ""; // show long message if available
            break;
        case "dependencyErrorsOnly":
            str = error.dependency || "";
            break;
        case "notEvalErrors":
            str = error.parse || error.dependency || ""; // show last error if any
            break;
        default:
            str = error.parse || error.dependency || error.evaluate || ""; // show last error if any
        }
        return str;
    }

    /**
     * Compile formula if needed, then check for errors:
     * Returns true if syntax or unknown symbol or depencency loop error
     * @param optionalDataSet
     * @returns {boolean}
     */
    checkForSyntaxOrReferenceError( optionalDataSet ) {
        this.findDependencies( optionalDataSet );
        return (this.getLastErrorString("notEvalErrors").length > 0); // ignore last evaluation error, if any
    }

    /** check for evaluation errors, and if any, return the number of total evaluations, and number which had errors.
     *  For example a formula may divide by some expression, resulting in 8 divide-by-zero errors in 100 cases. */
    checkforEvaluationError() {
        if( this.error.evaluate ) {
            return { total: this.evalCount(), errors: this.evalErrorCount() };
        }
        return null;
    }

    /**
     * Find the unknown symbols in the formula, and update the error state.
     * @param optionalDataSet
     * @returns {Array} -- array of names (strings)
     */
    findUnknownSymbols( optionalDataSet ) {
        // compile if needed, then return unknown symbols.
        this.findDependencies( optionalDataSet );
        if( Formula.compareNerdamerToMathJS && this.nerdamerString ) {
            this.nerdamerVariables = nerdamer(this.nerdamerString).variables();
            console.log("Dyn.Formula.findUnknownSymbols MathJS:", this.nodes.unknownSymbols, "Nerdamer:", this.nerdamerVariables );
        }
        return this.nodes.unknownSymbols;
    }

    /**
     * Update formula with multiplication (*) sign for unknown symbols which ends with x
     * Example: mx+b to m*x+b
     */
    static fixFormulaImplicitMultiply( str ) {
        let regex = /[a-zA-Z]+/g;
        let attributeRegexCheck = /<.*>/g;
        let matches;

        let attributeNameMatches = attributeRegexCheck.exec(str);
        // if formula string has attribute name, that means its coming from Add Attribute
        // it doesn't need to support implicit multiplication for x in the name
        if (attributeNameMatches && attributeNameMatches.length > 0){
            return str;
        }
        do {
            matches = regex.exec(str);
            if (matches && matches.length>0) {
                let symbol = matches[0];
                if(symbol.length > 1 && symbol.endsWith('x')) {
                    // TODO enhance the replacing logic for the inputs like `xmxxx * xxx`
                    str = str.replace(symbol, symbol.substring(0, symbol.length - 1) + '*x');
                }
            }
        } while (matches);
        return str;
    }

    /**
     * convert attribute names and IDs to valid symbols in formula string
     * NOTE: we must be mindful of name collisions,
     * e.g. with attribute names 'a','b', 'a b', and formula 'a-b', does formula change to "a_b"?
     * attribute "<length squared>" saved as "length_squared", etc.
     */
    fixFormulaAttributeName( str, attribute ) {
        var attributeName   = "<" + attribute.name + ">", // to help avoid name collisions require bracket around name
            attributeID     = attribute.id,
            attributeSymbol = Formula._attributeIDtoSymbol( attributeID );
        return str
            .replaceAll( attributeName, attributeSymbol )
            .replaceAll( attributeID, attributeSymbol );
    }

    /**
     * Use MathJS to parse the formula string, get symbols.
     * @param [dataSet] {Dyn.DataSet || null } -- dataSet, required only if allowing attribute names in formula
     * @param [attributeName] {Dyn.Attribute || null } -- optional attribute name for error messages
     */
    parse( dataSet, attributeName ) {

        // modify the formula string to be parsable by MathJS and/or Nerdamer
        this.formulaString = Formula.fixFormulaImplicitMultiply( this.formulaString );
        if( Formula.useNerdamerOnlyWherePossible || Formula.compareNerdamerToMathJS ) {
            this.nerdamerString = Formula.fixFormulaImplicitMultiply( this.nerdamerString );
        }
        if( dataSet ) {
            dataSet.attribs.forEach( (attr) => {
                this.formulaString = this.fixFormulaAttributeName( this.formulaString, attr );
            });
        }

        // initialize nodes and error state
        this.nodes = new Nodes( attributeName );
        this.error = {};
        this.compiledFormula = null;
        this.derivativeFormula = null;
        this.derivativeCompiled = null;

        // parse and analyze nodes of the formula (to find attributes, etc).
        let node;
        try {
            node = math.parse( this.formulaString );
            this.nodes.findNodes( node );
            let found = Aggregate.replaceAggregatesInString( this.formulaString );
            if( found ) {
                // re-compile with aggregate functions as case-independent constants
                // there is no need to change this.formulaString, only compiledFormula and scope
                this.nodes.aggregates = found.aggregates; // save for calcAggregates()
                node = math.parse( found.formulaString );
            }
            this.compiledFormula = node.compile();
        }
        catch(err) {
            console.log( "formula parse error {"+err.message+"}", this.nodes.selfSymbol );
            this.error.parse = this._errorMessageToLongString( err );
        }
    }

    /**
     * Calculate the aggregate function-attribute pairs found in the formula, and save as constants in the scope
     * For example: if '100-mean(age)' is the formula, and the mean of age is 1.23, add _mean_age:1.23 to the scope.
     * This works with findAggregates() which modifies the formula to replace 'mean(age)' with the constant '_mean_age',
     * so the evaluated formula is '100-_mean_age' with scope { _mean_age: 1.23, age: 20, ... }.
     * @param dataSet
     * @param scope
     */
    calcAggregates( dataSet, scope ) {
        this.nodes.aggregates.forEach((agg) => {
            let attribute = Formula._getAttribute( agg.attributeName, dataSet ),
                numericValues = attribute ? attribute.cases.filter( (v) => Number.isFinite(v)) : [];
            scope[ agg.variableName ] = agg.calculate( numericValues );
            console.log("Formula.calcAggregates", agg.variableName, scope[ agg.variableName ], this.formulaString );
        });
    }

    /**
     * Compile the formula, find dependencies (symbols) in the dataSet, set or clear the error state.
     * Must determine which symbols are valid attribute names, constants (pi, e), which are not.
     *   Updates:
     *      this.compiledFormula
     *      this.nodes.***Symbols (.attributeSymbols, .constantSymbols, etc).
     *      this.error.dependency
     * Note: Can be extended to find functions and operators in the formula (not yet needed)
     * @param [dataSet] {Dyn.DataSet||null} -- dataset which has attributes which we can reference
     * @param [attribute] {Dyn.Attribute||null} -- attribute which uses this formula
     * @returns {{}} - MathJS scope object with all values undefined, for use by getCaseScope() or getPlotFunctionScope().
     */
    findDependencies( dataSet, attribute ) {
        // compile the formula if needed (to be cleared when formula string changes)
        let attributeName = attribute ? Formula._attributeIDtoSymbol( attribute.id ) : "";
        if( ! this.compiledFormula ) {
            this.parse( dataSet, attributeName );
        }
        this.error.dependency = ""; // clear the dependency error state, unless we find an error below

        // add each symbol in formula to the appropriate list (attributeSymbols, etc)
        // update error.dependency state, save scope.
        let scope = {}; // symbol definitions for MathJS.eval()
        this.nodes.selfSymbol = attributeName; // update if formula attribute and renamed
        this.nodes.clearDependencies();
        this.nodes.symbols.forEach( ( symbol ) => {
            let attribute = dataSet && Formula._getAttribute( symbol, dataSet );
            if( attribute ) {
                // symbol matches an attribute
                this.nodes.attributeSymbols.push( symbol );
                // add a placeholder for this attribute in the scope (used later by getCaseScope())
                scope[symbol] = Dyn.dataUtil.kMissingValue;
            } else if( this._mathJS_Constants.includes( symbol )) {
                // MathJS constant like 'pi'; save it in our list and do not treat as an error
                this.nodes.constantSymbols.push( symbol );
            } else if( this.constantScope.hasOwnProperty( symbol )) {
                // symbol is an f(x) constant/variable like 'm','x','b', see also getPlotFunctionScope()
                this.nodes.plotSymbols.push( symbol );
                scope[symbol] = this.constantScope[symbol]; // value of constant or placeholder for variable
            } else if( this.nodes.functions.includes( symbol )) {
                //console.log("ignore symbol", symbol, "because it is a formula");
            // } else if( this.nodes.aggregates.find((agg)=>(agg.functionName===symbol))) {
            //     console.log("ignore symbol", symbol, "because it is an aggregate function");
            } else {
                // save unknown symbols and report the first one as an error
                if( ! ( this.error.parse || this.error.dependency )) {
                    this.error.dependency = Dyn.loc( "Unknown symbol: {{symbol}}", { symbol: symbol } );
                }
                this.nodes.unknownSymbols.push( symbol );
            }
        });
        return scope;
    }

        /**
         * return true if this formula depends on the given attribute. Requires that findDependencies()
         * has been called since the formula was created. Finds only direct dependencies, not indirect ones,
         * so caller should call this recursively to find all dependencies.
         * @param attributeID {Dyn.Attribute.id}
         */
    isDependentOn( attributeID ) {
        return !! this.nodes?.attributeSymbols?.includes( Formula._attributeIDtoSymbol( attributeID ));
    }

    /**
     * Get the attribute value format from our parsed formula
     *  format.decimals:0 means display as integer
     *  format.decimals:2 means round to two fixed decimal places
     *  format.decimals:undefined means no numeric format
     * @param dataSet {Dyn.DataSet}
     * @returns {{formulaDecimals: *}}
     */
    getValueFormat( dataSet ) {
        Dyn.assert( this.nodes, "Formula has not been parsed" );
        var newDecimals,
            oldDecimals;

        // scan attributes we reference, get the maximum number of significant decimals to display
        this.nodes.attributeSymbols.forEach( function( symbol ) {
            var attribute = Formula._getAttribute( symbol, dataSet );
            if( attribute ) {
                // save significant digit information from attributes we depend on, if any
                newDecimals = attribute.getNumericDecimals();
                if( newDecimals && oldDecimals ) {
                    oldDecimals = Math.max( oldDecimals, newDecimals );
                } else {
                    oldDecimals = newDecimals;
                }
            }
        } );
        return { formulaDecimals: newDecimals }; // undefined or 0+
    }

    /**
     * Find other attribute-formulas in the dataset that reference this attribute.
     * Updates: this.nodes.upstreamSymbols
     * @param dataSet {Dyn.DataSet}
     * @param thisAttribute {Dyn.Attribute}
     */
    findReverseDependencies( dataSet, thisAttribute  ) {
        // if parse error, we will not have a compiled formula, but nodes will initialized (and empty)
        Dyn.assert( this.nodes, "Formula has not been parsed");
        Dyn.assert( this.nodes.upstreamSymbols.length === 0 );
        Dyn.assert( thisAttribute.formula === this );
        var self = this,
            selfSymbol = this.nodes.selfSymbol = Formula._attributeIDtoSymbol( thisAttribute.id );

        dataSet.attribs.forEach( function ( otherAttribute ) {
            var otherFormula = otherAttribute.formula;
            if ( otherFormula && otherFormula.nodes.attributeSymbols.indexOf( selfSymbol ) !== -1 ) {
                var otherSymbol = Formula._attributeIDtoSymbol( otherAttribute.id );
                self.nodes.upstreamSymbols.push( otherSymbol );
            }
        } );
    }

    /**
     * Add zero or more formula attributes to the stack in the correct evaluation order,
     *      starting with the given attribute.  Designed to be called repeatedly if desired,
     *      e.g. to sort all formula attributes in the dataset.
     * If called for all attributes in the dataset, we want isUpstreamSearch===false,
     *      for a depth-first sort.
     * If called for a single attribute to find what other formula attributes depend on its values,
     *      we want isUpstreamSearch===true, for a depth-last sort.
     *
     * Details: if isUpstreamSearch==false, the given attribute is added only if it is a formula attribute.
     *      And if it references other attributes, those attributes are added first .
     * Example: Given stack [a] (from previous call), and attribute b which references c and d,
     *      and d is a formula attribute that references e,
     *      and all are formula attributes except for c,
     *      then stack will change to [a, e, d, b].
     *
     * @param attribStack {[Dyn.Attribute]} -- stack of formula attributes to evaluate from first to last
     * @param attribsVisited {[Dyn.Attribute]} -- list of formula attributes already visited (may or may not be in stack)
     * @param attr {Dyn.Attribute} -- attribute to start with (must be the attribute that owns this formula)
     * @param dataSet {Dyn.DataSet} -- the dataset that owns this attribute and formula
     */
    addAttributeToStackEvaluateOrder( attribStack, attribsVisited, attr, dataSet, isUpstreamSearch ) {

        function addFormulaAttributeDepthFirst( attr, isUpstreamSearch )
        {
            if ( attr.formula ) { // must be formula attribute
                attribsVisited.push( attr ); // visit attribute only once to avoid infinite loops
                if( isUpstreamSearch ) {
                    attribStack.push( attr );   // add depth-last if upstream search, so dependent attributes come after
                }
                var theNodes = attr.formula.nodes,
                    symbolList = isUpstreamSearch ? theNodes.upstreamSymbols : theNodes.attributeSymbols;
                symbolList.forEach( function ( symbol ) {
                    var referencedAttribute = Formula._getAttribute( symbol, dataSet );
                    if ( notVisited( referencedAttribute ) ) {
                        addFormulaAttributeDepthFirst( referencedAttribute, isUpstreamSearch );
                    }
                } );
                if( ! isUpstreamSearch ) {
                    attribStack.push( attr );   // add depth-first if downstream search, so dependent attributes come after
                }
            }
        }
        function notVisited( attr ) {
            return ( attribsVisited.indexOf( attr ) === -1 );
        }

        // recursive call to add attribute and its dependent formula-attributes to the stack
        Dyn.assert( this.nodes, "Formula has not been parsed");
        Dyn.assert( attr.formula === this, "must start with the attribute that owns this formula");
        addFormulaAttributeDepthFirst( attr, isUpstreamSearch );
    }

    /**
     * Check for depencency loops with formulas, where a references b and b references a.
     * If the given list of attributes is one we depend on then the next evaluate() should give an error.
     * @param attributeStack -stack of attribute names in evaluation order, created by addAttributeToStackEvaluateOrder()
     */
    checkForDependencyLoops( attributeStack, dataSet ) {
        // for each attribute that this formula references
        this.nodes.attributeSymbols.forEach( ( symbol ) => {
            // if attribute is in attribute stack, report a depencency loop
            var attribute = Formula._getAttribute( symbol, dataSet );
            if( attributeStack.indexOf( attribute ) !== -1 ) {
                this.error.dependency = Dyn.loc( "Dependency loop with {{symbol}}", { symbol: symbol } );
                console.log( "formula dependency error {"+this.error.dependency+"} for "+this.nodes.selfSymbol );
            }
        }, this);
    }

    /**
     * Get the attribute case values for attributes referenced in this formula.
     * @param scope {*} - reusable scope object
     * @param index - case index
     * @param dataSet {Dyn.DataSet} - dataSet with the case values
     */
    getCaseScope( scope, index, dataSet ) {
        //var self=this;
        Dyn.assert( this.nodes );
        scope.has__missing__values = false; // clear flag
        this.nodes.attributeSymbols.forEach( function( symbol ){
            let attribute = Formula._getAttribute( symbol, dataSet ),
                value;
            scope[symbol] = value = ( attribute ? attribute.cases[index] : Dyn.dataUtil.kMissingValue );
            if( Dyn.dataUtil.isMissing( value )) {
                scope.has__missing__values = true; // at least one missing value in this case
            }
        });
    }

    /**
     * Get the values of x and attributes referenced in this formula.
     * For evaluating a plot function y={some function of x}.
     * @param scope {*} - reusable scope object
     * @param xValue - point on plot to evaluate
     */
    getPlotFunctionScope( scope, xValue ) {
        scope.x = xValue; // all we need to do is update the x value in the scope, then call evaluate()
    }

    setConstant( name, value ) {
        Dyn.assert( typeof name === "string" );
        Dyn.assert( typeof name === "string" || typeof name === "number" || typeof name === "boolean");
        this.constantScope[ name ] = value;
    }

    /**
     * Evaluate the formula with the given scope, that resolves the value of referenced attributes
     * in the scope, e.g. { age:10, height:5.5 }. If a error.parse already set,
     * don't evaluate, just return the error until it is cleared.
     * Usually the caller evaluates the formula once for each case of an attribute.
     * @param scope {*} -- attributes and values referenced by MathJS formula
     * @returns {*} -- a number or string or undefined value; errors are shown as string values.
     */
    evaluate( scope, expectedType ) {
        var result;
        this.error.evaluate = ""; // clear last evaluate error, if any

        if ( this.getLastErrorString("notEvalErrors") || scope.has__missing__values ) {
            result = Dyn.dataUtil.kMissingValue; // if parse or dependency error, or if missing values in scope, show result as missing value (blank)
        } else {
            Dyn.assert( this.compiledFormula, "expecting a compiled formula");
            if( Formula.useNerdamerOnlyWherePossible && this.nerdamerString && !Formula.compareNerdamerToMathJS ) {
                result = this.evaluateNerdamer( scope, expectedType );
            } else {
                try {
                    // let MathJS evaluate it
                    this.numberOfEvaluations++; // for debugging
                    result = this.compiledFormula.eval( scope );
                    result = Formula.fixPrecision( result, result ); // fix precision if finite numeric
                    // check the type if asked for
                    if( expectedType ) {
                        let type = math.typeof( result ); // 'number', 'string', 'boolean', 'Unit', 'Complex', etc. see http://mathjs.org/docs/datatypes/index.html
                        if( type !== expectedType ) {
                            this.numberOfEvalErrors++;
                            this.error.evaluate = Dyn.loc( "Unexpected result: {{mathJStype}} data type", { mathJStype: type } );
                            result              = Dyn.dataUtil.kMissingValue;
                        }
                    }
                } catch ( err ) {
                    // show new evaluate error
                    this.numberOfEvalErrors++;
                    this.error.evaluate = this._errorMessageToLongString( err );
                    result              = Dyn.dataUtil.kMissingValue;
                    //console.log( "formula evaluate error {" + err.message + "}" + ( ( this.nodes && this.nodes.selfSymbol ) ? "" : " for " + this.nodes.selfSymbol ) );
                }
            }
            if( Formula.compareNerdamerToMathJS ) {
                let nerdResult = this.evaluateNerdamer( scope, expectedType );
                let nerdResultIsClose = (Dyn.mathUtil.isNumber( result )) ?
                    Dyn.mathUtil.isClose( result, Number(nerdResult)) :
                    Dyn.mathUtil.isNumber( result )===Dyn.mathUtil.isNumber(Number(nerdResult)) /* if one non-finite, the other should also be non-finite, e.g. null, -Infini   ty, etc */
                if( ! nerdResultIsClose ) {
                    this.debugNotCloseAssertCount = 1+(this.debugNotCloseAssertCount||0);
                }
                Dyn.assert( nerdResultIsClose || (this.debugNotCloseAssertCount > 3), // assert a max of 3 times for this issue for this formula
                    "Dyn.formula.evaluate MathJax:"+result+" differs from Nerdamer:"+nerdResult+" for "+this.nerdamerString+", x="+scope.x+", diff:"+(result-Number(nerdResult)));

            }
        }
        return( Dyn.dataUtil.normalizeCaseValueString( result )); // make sure result is string, number or undefined
    }

    /** equivalent to evaluate() but using Nerdamer instead of MathJS.
     * NOTE: consider in the future to use just Nerdamer and drop MathJS, to simplify.
     * NOTE: We should consider using nerdamer.buildFunction(), it might be more efficient for plotting
     * or evaluating over many cases. See https://nerdamer.com/functions/Expression.buildFunction.html
     */
    evaluateNerdamer( scope, expectedType ) {
        //let localNerdamerForDebugging = nerdamer;
        let result;
        Dyn.assert( expectedType==='number' );
        try {
            // let MathJS evaluate it
            if( this.nerdamerString ) {
                this.numberOfEvaluations++; // for debugging
                let nerdScope = Dyn.dataUtil.clone( scope ); // nerdamer modifies the scope so we must copy it
                let resultText = nerdamer( this.nerdamerString, nerdScope, "numer" ).text();
                result = Formula.fixPrecision( Number( resultText ), Number.NaN ); // convert to finite or NaN
                // better? result = nerdamer( this.nerdamerString ).evaluate( nerdScope, "numer" ).text();
            }
        }
        catch ( err ) {
            // show new evaluate error
            this.numberOfEvalErrors++;
            this.error.evaluate = this._errorMessageToLongString( err );
            result = this._errorMessageToShortString( err );
            if( !( this._errorMessageIsDivideByZero( err ) && Formula.dontLogDivideByZeroError )) {
                console.log( "formula evaluate error {" + err.message + "} for " + this.nerdamerString );
            }
        }
        return( Dyn.dataUtil.normalizeCaseValueString( result )); // make sure result is string, number or undefined
    }

    /**
     * Get the slope of the function at the give x,y point.
     * @param x -- x value of F(x) function
     * @param y -- y value of F(x) function; assumes we called Evaluate() first to get this.
     * @param scope -- scope from GetPlotFunctionScope
     * @returns {number|NaN}
     */
    slopeOfFx( x, y, scope ) {
        // reference: https://mathjs.org/docs/reference/functions/derivative.html
        //            e.g. math.derivative('x^2', 'x').evaluate({x: 4})
        let slope;
        if( Dyn.mathUtil.isNumber(x) &&
            Dyn.mathUtil.isNumber(y) && // if y not finite, don't bother with slope
            this.compiledFormula ) {    // formula must have been parsed already
            // parse and compile the derivative once only
            if( ! this.derivativeFormula ) {
                try {
                    let node = math.derivative( this.formulaString, 'x' );
                    this.derivativeCompiled = node.compile();
                } catch ( err ) {
                    console.log( "formula derivative parse error {" + err.message + "} for " + this.formulaString );
                    this.derivativeCompiled = null;
                }
            }
            // calculate the slope by evaluating the derivative at x
            if( this.derivativeCompiled ) {
                try {
                    this.numberOfEvaluations++; // for debugging
                    slope = this.derivativeCompiled.eval( scope );
                }
                catch ( err ) {
                    console.log( "formula derivative evaluate error {"+err.message+"} for "+this.formulaString );
                }
            }
        }
        return slope; // caller should check Dyn.mathUtil.isNumber(slope)
    }

    /**
     * Return an array of x axis values to show the location of
     *      finite numeric roots, local min/max, and intercepts for the function of x.
     * Y-axis intercept: f(x) where x=0
     * X-axis intercept: f(x) where y=0 : solve for y=0 to get x value(s) if any
     * local Min/Max: solve slope(f(x)) where slope = 0, to get x value(s) if any
     * See https://nerdamer.com/functions/solveFor.html for limitations, e.g. not function of x, or not a polynomial, etc etc.
     * @param scope -- scope from getPlotFunctionScope()
     * @returns {[]} -- array of finite numeric roots (length 0+)
     */
    rootsAndInterceptsOfFx( scope ) {
        let localMinMax = [], // array of x-axis values of local min,max (slope==0)
            intercepts =[0]; // add y-axis intercept value to start
        let formulaOfX, rootsFormula, rootsResult, slopeFormula, slopeResult;
        formulaOfX = rootsFormula = rootsResult = slopeFormula = slopeResult = "";
        //let localNerdamerForDebugging = nerdamer;
        function logError( err, formulaString ) {
            console.log( "roots evaluation error {"+err.message+"} for "+formulaString, scope, rootsFormula, rootsResult, slopeFormula, slopeResult );
        }
        function resultAsArrayOfFiniteNumbers( resultText ) {
            // convert results array, e.g [ 0, "1.3", "2.345", "1.3*i", "NaN", "-Infinity" ],
            //      to array of finite numbers, discarding imaginary roots, etc.
            return resultText.substring( 1, rootsResult.length - 1 ).split(',')
                .map((str) => Number(str))
                .filter((n) => isFinite(n));
        }
        if( this.nerdamerString ) {
            try {
                // get values from formula like 'a*x^2', {a:6.2}, convert to '6.2*x^2', then solve for 'x=0', and 'slope(x)=0'.
                //rootsExpr     = 'roots(' + ( formulaOfX.toString() ) + ')'; // don't use roots(), see https://github.com/jiggzson/nerdamer/issues/510
                let nerdScope = Dyn.dataUtil.clone( scope ); // copy because nerdamer changes it
                delete nerdScope.x; // keep 'x' undefined
                formulaOfX = nerdamer( this.nerdamerString ).evaluate( nerdScope ).toString(); // replace constants like 'a' with numbers like '6.2'
                this.numberOfEvaluations++; // for debugging
            } catch ( err ) {
                logError( err, this.nerdamerString );
            }
            if( formulaOfX ) {
                try {
                    rootsFormula = 'solve(' + ( formulaOfX ) + ',x)';
                    rootsResult  = nerdamer( rootsFormula ).evaluate().text(); // solve for x=0 to get x-axis intercepts
                    intercepts = intercepts.concat( resultAsArrayOfFiniteNumbers( rootsResult )); // add finite numeric x-intercepts
                    this.numberOfEvaluations++; // for debugging
                } catch ( err ) {
                    logError( err, this.nerdamerString );
                }
                try {
                    slopeFormula = 'solve(diff(' + ( formulaOfX ) + ',x),x)';
                    slopeResult  = nerdamer( slopeFormula ).evaluate().text(); // solve for derivative of x=0 to get local min, max
                    localMinMax = resultAsArrayOfFiniteNumbers( slopeResult ); // add finite numeric local min, max
                    this.numberOfEvaluations++; // for debugging
                } catch ( err ) {
                    logError( err, this.nerdamerString );
                }
                // convert results array, e.g [ 0, "1.3", "2.345", "1.3*i", "NaN", "-Infinity" ],
                //      to array of unique finite numbers, discarding imaginary roots, duplicates, etc.
                intercepts = intercepts.concat( localMinMax ) // append slope==0
                    .filter( (n,i,self) => ( isFinite(n) && (self.indexOf(n)===i)) ); // remove NaN and duplicate values
                // console.log( "Dyn.formula.rootsAndInterceptsOfFx() : ",
                //     "intercepts ("+intercepts.length+")", intercepts,
                //     "localMinMax ("+localMinMax.length+")", localMinMax /*, rootsResult, slopeResult */);
            }
        }
        return intercepts; // array of x-axis values which have x-intercepts, y-intercepts, or slope==0.
    }

    /**
     * Convert attribute ID string to symbol for Math.js parsing
     * Symbol: a Math.js variable name; Math.js disallows underscores, names starting with a digit, etc.
     * @param attributeID {string}
     * @returns {string}
     * @private
     */
    static _attributeIDtoSymbol( attributeID ) {
        // attribute IDs are created by sluggify() function
        // reference for valid characters in MathJS variable names: https://github.com/josdejong/mathjs/issues/688
        return attributeID
                .replace(/\-/g, '_') // replace dash with underbar, so it is not interpreted as a minus operator
                .replace(/^([0-9])/g, "_$1"); // add underbar if the first character is a digit
    }

    /**
     * Find the attribute object in the dataset that matches this symbol name.
     * @param attributeSymbol {string} -- attribute symbol from _attributeIDtoSymbol()
     * @param dataSet {Dyn.DataSet}
     * @returns {Dyn.attribute||undefined}
     * @private
     */
    static _getAttribute( attributeSymbol, dataSet ) {
        // attribute symbols have underbar, attribute IDs have dash or underbar.
        // Note: we can't use: symbol.replace(/_/g, '-')
        //       because we don't know if dash should be an underbar or stay a dash.
        return dataSet.attribs.find( (attr) => (attributeSymbol === Formula._attributeIDtoSymbol( attr.id )));
    }

    /**
     * Convert a MathJS exception object to a case value string, so the user can see the error.
     * @param err
     * @returns {string}
     * @private
     */
    _errorMessageToShortString( err ) {
        if( /is not a function/.test( err.message )) {
            return Dyn.loc( "not a function" );
        } else {
            let short = /^[\w]+\s[\w]+/.exec( err.message );
            if( short ) {
                return short[0];
            }
        }
        return err.message;
    }

    /**
     * Convert a MathJS exception object to a long string, so the user can see the error.
     */
     _errorMessageToLongString( err ) {
        // for now just return the string, maybe in the future use other error information provided, e.g. err.stack
        return err.message;
    }

    _errorMessageIsDivideByZero( err ) {
        // return true if "Divide by zero" is in error message thrown by Nerdamer
        return /Division by zero/.test( err.message );
    }

    latexFormulaToMathJS( formulaString) {
        let mjsString = formulaString
            .replace(/\\sqrt\[(.*?)\]\{(.*?)\}/g, 'nthRoot($2,$1)') // nth root '\sqrt[n]{x}\ ' => 'nthRoot(x,n)'
            .replace(/(\\frac){(.*?)}{(.*?)}/g, '(($2)/($3))')    // division '\frac{a}{b}' => '((a)/(b))' || 'divide((a),(b))'
            .replace(/\\cdot/g, '*' )                           // multiplication symbol: \cdot => *
            .replace(/{/g, '(').replace(/}/g, ')')              // curley brackets {} with ()
            .replace(/\\left\(/g, '(').replace(/\\right\)/g, ')') // \left and \right with ()
            .replace(/\\/g, '') // do last                      // remove \
        console.log("LaTeX formula '", formulaString, "' convert to MathJS '", mjsString, "'" );
        return mjsString;
    }

    latexFormulaToNerdamer( formulaString) {
        let nerdString = formulaString
            .replace(/\\sqrt\[(.*?)\]\{(.*?)\}/g, '(($2)^(1/($1)))') // nth root '\sqrt[n]{x}\ ' => '((x)^(1/(n))'
            .replace(/(\\frac){(.*?)}{(.*?)}/g, '(($2)/($3))')    // division '\frac{a}{b}' => '((a)/(b))' || 'divide((a),(b))'
            .replace(/\\cdot/g, '*' )                           // multiplication symbol: \cdot => *
            .replace(/{/g, '(').replace(/}/g, ')')              // curley brackets {} with ()
            .replace(/\\left\(/g, '(').replace(/\\right\)/g, ')') // \left and \right with ()
            .replace(/\\/g, '') // do last                      // remove \
        try {
            // *DON'T USE, replaces "k/x" with "k*x^(-1)"
            //nerdString = nerdamer.convertFromLaTeX( nerdString ).text();
        } catch ( err ) {
            console.log( "formula error in nerdamer.convertFromLaTeX() {" + err.message + "} for " + formulaString );
            this.error.parseNerdamer = err.message;
            nerdString = "";
        }
        console.log("LaTeX formula '", formulaString, "' convert to Nerdamer '", nerdString, "'" );
        return nerdString;
    }

    /** reset counts for error reporting and debugging */
    evalCountStart() {
        this.numberOfEvaluations = 0; // total number of formula evaluations for this round
        this.numberOfEvalErrors = 0; // number of these evaluations that resulted in an error
    }

    evalCount() {
        return this.numberOfEvaluations;
    }

    evalErrorCount() {
        return this.numberOfEvalErrors;
    }

    /** limit the calculated values from formulas
     *   - in floating-point precision (to avoid calculation roundoff errors after 15 decimal places)
     *   - in decimal places after zero (to avoid scientific notation in calculations, e.g. sin(pi)=0 not 1e-16
     */
    static fixPrecision( num, valueIfNotFiniteNumeric ) {
        return Number.isFinite( num ) ?
            parseFloat( parseFloat( num.toPrecision( Dyn.mathUtil.kStatsMaxPrecision )).toFixed( Dyn.mathUtil.maxCalcFixedDecimals)) :
            valueIfNotFiniteNumeric;
    }

    static unitTest() {
        let unitTester = new Dyn.UnitTester( "Dyn.Formula.unitTest()" );

        function testNerdamerParse( parsed, expected ) {
            var result = parsed.text();
            unitTester.test( result===expected, "NERDAmer formula parse error: "+" : '"+result+"' !== '"+expected+"'");
        }
        testNerdamerParse( nerdamer('x^2+2*(cos(x)+x*x)'), '2*cos(x)+3*x^2' );
        testNerdamerParse( nerdamer('cos(x)+(y-x)^2', {x:7}, ['expand', 'numer']), '(-7+y)^2+0.753902254343304582');

        // test aggregate functions
        let values = [ 1, 2, 3, 3, 3 ],
            aggFormulas = [
                // formula, expected-result, optional equivalent mathJS formula.  Note that attribute name is ignored.
                [ "mean(age)", 2.4 ],
                [ "median(v)", 3],
                [ "iqr(v_v)", 1 ],
                [ "stdDev( v_v )", 0.89442719099992],
                [ "meanAvDev( v_v )", 0.72 ],
                [ "variance(abc)", 0.8 ],
                [ "sum(v)", 12 ],
                [ "min(v)", 1 ],
                [ "max(v)", 3 ],
                [ "count(v)", 5 ],
            ];
        aggFormulas.forEach((formulaResult) => {
            let [str, expected /*, mathJSresult*/ ] = formulaResult;
            let aggs = Aggregate.replaceAggregatesInString( str );
            unitTester.test( aggs.aggregates.length === 1, "could not find 1 aggregate function in:"+str);
            aggs.aggregates.forEach( (agg) => {
                let result = agg.calculate( values );
                unitTester.test( Dyn.mathUtil.isClose(result,expected), agg.formulaString+" result:"+result+" !== "+expected );
            });
        });
        let agg3 = [ "1 + mean(v) - median(v) + count( v_V )", 3], // test with multiple aggregates in the formula
            agg3r = Aggregate.replaceAggregatesInString( agg3[0] );
        unitTester.test( agg3r.aggregates.length===agg3[1], agg3[0]+"did not find the "+agg3[1]+" aggregate formulas" );

        function testMathJSEval( formulaString, expectedResult ) {
            let actualResult = math.eval( formulaString );
            unitTester.test( Dyn.mathUtil.isClose( actualResult, expectedResult ), formulaString + " result:" + actualResult + " !== " + expectedResult + " from MathJS" );
        }
        testMathJSEval( "mean(1,2,3,3,3)", 2.4 );
        testMathJSEval( "median(1,2,3,3,3)", 3 );
        testMathJSEval( "std(1,2,3,3,3)", 0.89442719099992 );

        unitTester.report();
        return true;
    }

}  // class Formula

Dyn.Formula = Formula; // make class public

Dyn.debugFormula_UnitTestHasRun = false;
setTimeout( () => { // delay the test until mathUtil.js is loaded
    if( Dyn.debugging ) {
        Dyn.debugFormula_UnitTestHasRun = Formula.unitTest();
    }
}, 100 );

};  // wrapDynFormula
